import Box from '@components/base/Box'
import Tooltip from '@components/tootilp/Tooltip'
import { getParentBg, useTheme } from '@libs/useTheme'
import {
  PASSWORD_PATTERN,
  TEXT_PATTERN,
  XSS_PATTERN,
} from '@libs/Utility/validation'
import { Maybe } from 'graphql/jsutils/Maybe'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { MdVisibility, MdVisibilityOff } from 'react-icons/md'
import { isReadable } from 'tinycolor2'
import { BaseInputProps } from './BaseInputProps'
import { getDefaultValidations, validate } from './Validations'

export interface TextInputProps extends Omit<BaseInputProps, 'error'> {
  /**
   * The type of the input.
   */
  type?: 'text' | 'password' | 'email' | 'tel' | 'number'
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onBlur?: () => void
  border?: 'border' | 'border-none'
  max?: number
  min?: number
  textColour?: string
  name?: string
  labelBgColour?: string
  show?: boolean
  customClassLabel?: string
  autoComplete?: string
  setError?: (message: string, testId: string) => void
  ignoreDefaultValidations?: boolean
  forceLabel?: boolean
  error?: string | ReactNode
}

/**
 * Primary UI component for text input
 */
export const TextInput = ({
  label,
  placeholder,
  value = '',
  onChange,
  className,
  hint,
  validations,
  required,
  type = 'text',
  border = 'border',
  onKeyDown,
  onBlur,
  max,
  min,
  textColour,
  disabled,
  testid,
  name,
  tooltip,
  show = true,
  customClassLabel = '',
  error = undefined,
  setError,
  autoComplete,
  ignoreDefaultValidations = false,
  variation = 'v1',
  forceLabel = false,
  disableInlineErrors = false,
}: TextInputProps) => {
  const fieldTypeValidations = ignoreDefaultValidations
    ? validations
    : getDefaultValidations(type, validations)

  let errorMessage = error ?? validate(value, fieldTypeValidations)
  // if (errorMessage)
  // console.log({ errMessage: errorMessage, fieldTypeValidations })

  const [showTooltip, setShowTooltip] = useState(true)
  const [showPassword, setShowPassword] = useState<boolean>(false)

  useEffect(() => {
    // Autohide tooltip if the input is not required.
    if (tooltip?.toLowerCase().includes('require') && !required)
      setShowTooltip(false)
  }, [tooltip, required])

  // useEffect(() => {
  //   if (errorMessage) console.log({ errorMessage, fieldTypeValidations })
  // }, [errorMessage])

  if (value === null) value = ''

  const generateStyles = useMemo(() => {
    let base = `transition-all outline-none rounded bg-white/70 focus:ring-0`
    let errorStyles = errorMessage
      ? 'text-red-500 border-red-500 focus:border-red-500'
      : 'border-gray-300 focus:border-forumm-blue'
    const disabledStyles = disabled
      ? '!text-gray-500 bg-gray-100 border-gray-300 cursor-not-allowed'
      : ''
    let textStyles = 'text-sm placeholder-gray-500'
    let positionStyles = 'h-11 px-3 py-2'
    let sizeStyles = 'w-full'
    const passwordStyles = type === 'password' ? 'pr-11' : ''

    if (variation === 'v2') {
      base = `!bg-white rounded-none border-black`
      errorStyles = errorMessage ? 'text-red-500 border-red-500' : ''
      textStyles = 'text-sm placeholder-black font-semibold'
      positionStyles = 'px-4 py-4'
    }

    const payload = `${base} ${textStyles} ${positionStyles} ${border} ${errorStyles} ${disabledStyles} ${passwordStyles} ${sizeStyles} ${className}`
    return payload
  }, [border, className, disabled, errorMessage, type, variation])

  const input = useMemo(
    () => (
      <input
        data-testid={testid ?? null}
        required={required}
        value={value}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onChange={(e) => {
          const val = e.target.value
          errorMessage = validate(val, fieldTypeValidations)
          // console.log({ errorMessage, target: val, fieldTypeValidations })
          // setErrorMessage(errorMsg ?? '')
          const isInvalid = errorMessage !== ''
          if (!!setError && typeof errorMessage === 'string')
            setError(errorMessage, testid ?? label ?? 'unknown')
          if (!!onChange) onChange(val, isInvalid)
        }}
        max={max}
        disabled={disabled}
        min={min}
        type={showPassword ? 'text' : type}
        placeholder={placeholder}
        autoComplete={autoComplete ?? label}
        id={label}
        className={generateStyles}
        style={{ color: textColour }}
      />
    ),
    [
      testid,
      required,
      value,
      onKeyDown,
      onBlur,
      onChange,
      max,
      disabled,
      min,
      type,
      placeholder,
      autoComplete,
      label,
      generateStyles,
      textColour,
      errorMessage,
      fieldTypeValidations,
      setError,
      showPassword,
    ]
  )

  const labelRef = useRef<HTMLSpanElement>(null)

  const showLabel = useMemo(
    () => (label && variation === 'v1') || forceLabel,
    [label, variation, forceLabel]
  )

  if (!show) return <></>
  return (
    <label
      className={['flex flex-col my-3', className].join(' ')}
      style={{ color: textColour }}
    >
      {showLabel && (
        <span
          ref={labelRef}
          className={`mb-1.5 text-sm font-medium leading-5 transition-all ${
            errorMessage ? 'text-red-500' : ''
          }`}
        >
          <Tooltip
            show={showTooltip}
            tooltip={tooltip}
            className={`flex flex-col`}
          >
            <span
              className={`z-10 
                ${variation === 'v2' ? 'text-black font-semibold' : ''}
                ${customClassLabel} 
                ${required ? "after:content-['*'] after:ml-1 after:text-red-500" : ''}`}
            >
              {label}
            </span>
          </Tooltip>
        </span>
      )}
      <span className="relative">
        {input}
        {type === 'password' && (
          <button
            type="button"
            className="absolute right-3 top-1/2 flex -translate-y-1/2 cursor-pointer items-center text-lg"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <MdVisibility /> : <MdVisibilityOff />}
          </button>
        )}
      </span>
      {errorMessage && !disableInlineErrors && (
        <Box
          ignoreTheme
          textColour="foregroundTextColour"
          className="transition-all text-red-500 text-xs font-bold mt-1"
        >
          {errorMessage}
        </Box>
      )}
      {hint && (
        <span className="mt-1 mb-4 ml-2 text-xs" style={{ color: textColour }}>
          {hint}
        </span>
      )}
    </label>
  )
}
